import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, adminMe } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(true);

  // If a valid session cookie already exists, redirect straight to /admin.
  useEffect(() => {
    let cancelled = false;
    adminMe()
      .then(() => { if (!cancelled) navigate("/admin", { replace: true }); })
      .catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid email or password";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-ink/55">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10 group" data-testid="admin-back-home">
          <img src="/logo.jpg" alt="Aotūroa Academy" className="h-11 w-auto rounded-md" />
          <span className="text-sm text-ink/60 group-hover:text-ink transition-colors">← Back to website</span>
        </Link>

        <div className="bg-white border border-stone rounded-3xl p-8 sm:p-10">
          <div className="w-12 h-12 rounded-xl bg-ink flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-crimson" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-ink">Admin sign in</h1>
          <p className="mt-2 text-sm text-ink/55">Access form submissions and subscribers.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                data-testid="admin-login-email"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink/80 mb-2 block">Password</label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                data-testid="admin-login-password"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="text-sm text-crimson bg-crimson/5 border border-crimson/20 rounded-lg px-4 py-3" data-testid="admin-login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in..." : <>Sign in <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
